import React from "react";
import { NavLink, Route, Switch } from "polyrouter";
import { MenuItem } from "store/menu";
import i18n, { useTranslation } from "utilities/i18n";
import { OptimizelyFeature } from "@optimizely/react-sdk";
import { ProtectedNavLink, Guard } from "../../TsRoleGuards";
import { needsAny, Requirement } from "rbac";
import { useSelector } from "react-redux";
import { State } from "store";
import { PolyIcon } from "elements";

export interface MenuProps {
  items: Array<MenuItem>;
  className?: string;
}

export interface SubMenuProps {
  item: MenuItem;
  active: boolean;
  debugUserRoles?: boolean;
}

const menuItemPermission = function(subItem: MenuItem): Requirement {
  const perm = subItem.permission;

  if (typeof perm === "function") {
    return perm;
  }
  if (perm.length) {
    return needsAny(perm);
  }
  return needsAny([]);
};

const SubMenuItemComponent: React.FC<{
  item: MenuItem;
}> = ({ item: { url, persist, icon, name, permission } }) => {
  const { t } = useTranslation();
  const NavProps = {
    className: "ph-15 pv-5 mv-14 text-left font-sm d-block",
    key: `submenu-${t(name)}`,
    persist: persist,
    to: url
  };
  const content = (
    <>
      {icon && <PolyIcon className="mr-5 white" icon={icon} />}
      {t(name)}
    </>
  );

  if (permission.length) {
    const req = Array.isArray(permission) ? needsAny(permission) : permission;
    return (
      <ProtectedNavLink
        data-testid="sub-menu-item"
        {...NavProps}
        requirement={req}
      >
        {content}
      </ProtectedNavLink>
    );
  } else {
    return (
      <NavLink data-testid="sub-menu-item" {...NavProps}>
        {content}
      </NavLink>
    );
  }
};

const MenuEntry: React.FC<{
  item: MenuItem;
}> = ({ item }) => {
  if (item.optimizely) {
    return (
      <OptimizelyFeature feature={item.optimizely}>
        {isEnabled => isEnabled && <SubMenuItemComponent item={item} />}
      </OptimizelyFeature>
    );
  }

  return <SubMenuItemComponent item={item} />;
};

export class SubMenuDropdown extends React.Component<SubMenuProps> {
  state = {
    open: false
  };

  handleClick = (e: MouseEvent) => {
    if (!this?.node?.contains(e.target as Node)) {
      this.setState({ open: false });
    }
  };

  componentDidMount = () => {
    document.addEventListener("mousedown", this.handleClick, false);
  };

  node: HTMLDivElement | null = null;

  render() {
    const { item, active, debugUserRoles } = this.props;
    const { open } = this.state;

    if (!item.subMenus.length) {
      return null;
    }

    const divider = <li key={`div-${item.id}`} className="divider d-block" />;
    const dropdownClasses = [
      "mv-0",
      open && "open",
      debugUserRoles && open && "allowed"
    ]
      .filter(c => !!c)
      .join(" ");
    const menuTriggerClasses = [
      "sub-menu-trigger font-bold clickable pb-15 ph-1 d-block",
      active && "active"
    ]
      .filter(c => !!c)
      .join(" ");

    return (
      <div ref={node => (this.node = node)}>
        <span
          onClick={() => this.setState({ open: !open })}
          className={menuTriggerClasses}
          data-testid="dropdown-trigger"
        >
          {i18n.t(item.name)}
        </span>
        <ul
          key="sub-menu"
          className={dropdownClasses}
          data-testid={"dropdown-content"}
        >
          {item.subMenus.map((subItem, index) => {
            const { id, kind, subMenus } = subItem;
            if (kind === "simple") {
              return (
                <li
                  key={id}
                  data-testid="menu-entry"
                  onClick={() => this.setState({ open: false })}
                >
                  <MenuEntry item={subItem} />
                </li>
              );
            }
            return (
              <React.Fragment key={index}>
                {/**Map over the permissions needed for the group of menu items to conditionally render the divider*/}
                {index !== 0 && (
                  <Guard requirement={menuItemPermission(subItem)}>
                    {divider}
                  </Guard>
                )}
                {subMenus.map(item => (
                  <li
                    key={item.id}
                    data-testid="menu-entry"
                    onClick={() => this.setState({ open: false })}
                  >
                    <MenuEntry item={item} />
                  </li>
                ))}
              </React.Fragment>
            );
          })}
        </ul>
      </div>
    );
  }
}

export const MenuItemComponent: React.FC<MenuItem> = (item: MenuItem) => {
  const myUrl = (item.subMenus[0] || item).url;
  const parent = item.subMenus.length >= 1;
  const debugUserRoles = useSelector((state: State) => {
    return state.env.debugUserRoles;
  });

  const { t } = useTranslation();

  return (
    <li key={item.id} className="d-inline-block">
      {parent ? (
        <Switch>
          <Route path={item.url}>
            <SubMenuDropdown
              item={item}
              active={true}
              debugUserRoles={debugUserRoles}
            />
          </Route>
          <Route>
            <SubMenuDropdown
              item={item}
              active={false}
              debugUserRoles={debugUserRoles}
            />
          </Route>
        </Switch>
      ) : (
        <NavLink className="font-bold pv-15 ph-1 d-block" to={myUrl}>
          {t(item.name)}
        </NavLink>
      )}
      <Route path={item.url} />
    </li>
  );
};

/**
 * Only used in TopBar.
 */
export const Menu: React.FC<MenuProps> = ({ items, className }) => {
  const classes = ["menu animated horizontal nu-3", className]
    .filter(c => !!c)
    .join(" ");

  return (
    <ul className={classes}>
      {items.map(m => (
        <MenuItemComponent key={m.id} {...m} />
      ))}
    </ul>
  );
};

export default Menu;
